import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { 
  QrCode, 
  Shield, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle2, 
  Copy,
  Users,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import { 
  generateScannerPassword,
  validateScannerUsername,
  validateScannerPassword 
} from '@/lib/attendance/scanner-auth-utils';

interface BusinessScannerCredentialsManagerProps {
  businessId: string;
  businessName?: string; // Optional - will be fetched if not provided
  onUpdate?: () => void;
}

interface BusinessScannerConfig {
  username: string | null;
  password: string | null;
  enabled: boolean;
  updatedAt: string | null;
}

const BusinessScannerCredentialsManager: React.FC<BusinessScannerCredentialsManagerProps> = ({
  businessId,
  businessName: providedBusinessName,
  onUpdate
}) => {
  const [config, setConfig] = useState<BusinessScannerConfig>({
    username: null,
    password: null,
    enabled: false,
    updatedAt: null
  });
  const [businessName, setBusinessName] = useState<string>(providedBusinessName || '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);
  
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(false);
  
  // Validation state
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load current business scanner configuration
  useEffect(() => {
    loadScannerConfig();
  }, [businessId]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isSetupOpen) {
      resetFormToCurrentValues();
    }
  }, [isSetupOpen]);

  // Reset form to current saved values
  const resetFormToCurrentValues = (data?: any) => {
    const configData = data || {
      scanner_username: config.username,
      scanner_password: config.password,
      scanner_enabled: config.enabled
    };
    
    setUsername(configData.scanner_username || '');
    setPassword(configData.scanner_password || '');
    setEnabled(configData.scanner_enabled || false);
    // Clear any errors
    setUsernameError(null);
    setPasswordError(null);
    setSaveError(null);
  };

  const loadScannerConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('businesses')
        .select('name, scanner_username, scanner_password, scanner_enabled, scanner_updated_at')
        .eq('id', businessId)
        .single();

      if (error) {
        console.error('Error loading business scanner config:', error);
        toast.error("Failed to load scanner configuration");
        return;
      }

      // Set business name if not provided as prop
      if (!providedBusinessName && data.name) {
        setBusinessName(data.name);
      }

      setConfig({
        username: data.scanner_username,
        password: data.scanner_password,
        enabled: data.scanner_enabled || false,
        updatedAt: data.scanner_updated_at
      });

      // Set form defaults - this will be called both on initial load and when resetting form
      resetFormToCurrentValues(data);

    } catch (error) {
      console.error('Error loading business scanner config:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    // Validate username
    const usernameValidation = validateScannerUsername(username);
    if (!usernameValidation.valid) {
      setUsernameError(usernameValidation.error || 'Invalid username');
      isValid = false;
    } else {
      setUsernameError(null);
    }
    
    // Validate password (only if it's being changed)
    if (password) {
      const passwordValidation = validateScannerPassword(password);
      if (!passwordValidation.valid) {
        setPasswordError(passwordValidation.error || 'Invalid password');
        isValid = false;
      } else {
        setPasswordError(null);
      }
    } else if (!config.username) {
      // Password required for new setup
      setPasswordError('Password is required for new scanner setup');
      isValid = false;
    } else {
      setPasswordError(null);
    }
    
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Update directly using plain text password
      const updateData: any = {
        scanner_username: username,
        scanner_enabled: enabled,
        scanner_updated_at: new Date().toISOString()
      };

      // Add password if provided (plain text)
      if (password) {
        updateData.scanner_password = password;
      }

      const { error } = await supabase
        .from('businesses')
        .update(updateData)
        .eq('id', businessId);

      if (error) {
        throw error;
      }

      toast.success(`Scanner access has been ${enabled ? 'enabled' : 'disabled'} for ${businessName}`);

      // Reload configuration
      await loadScannerConfig();
      
      // Clear form
      setPassword('');
      setIsSetupOpen(false);

      if (onUpdate) {
        onUpdate();
      }

    } catch (error: any) {
      console.error('Error updating business scanner credentials:', error);
      setSaveError(error.message || 'Failed to update scanner credentials');
      
      toast.error(error.message || 'Failed to update scanner credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (newEnabled: boolean) => {
    if (!config.username && newEnabled) {
      // Need to set up credentials first
      setIsSetupOpen(true);
      return;
    }

    setEnabled(newEnabled);
    
    // If disabling, save immediately
    if (!newEnabled) {
      try {
        setSaving(true);
        const { error } = await supabase
          .from('businesses')
          .update({ 
            scanner_enabled: false,
            scanner_updated_at: new Date().toISOString()
          })
          .eq('id', businessId);

        if (error) throw error;

        toast.success(`Scanner access has been disabled for ${businessName}`);

        await loadScannerConfig();
        
        if (onUpdate) {
          onUpdate();
        }
      } catch (error: any) {
        console.error('Error disabling business scanner:', error);
        setEnabled(!newEnabled); // Revert
        toast.error("Failed to disable scanner access");
      } finally {
        setSaving(false);
      }
    }
  };

  const generatePassword = () => {
    const newPassword = generateScannerPassword();
    setPassword(newPassword);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading scanner configuration...
      </div>
    );
  }

  return (
    <Dialog open={isSetupOpen} onOpenChange={setIsSetupOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm"
          variant="outline"
          className="w-full"
          disabled={saving}
        >
          <QrCode className="h-3 w-3 mr-2" />
          Scanner Access
        </Button>
      </DialogTrigger>
          
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Scanner Access for {businessName}
          </DialogTitle>
          <DialogDescription>
            Set up scanner credentials for your staff to update customer passes across all loyalty programs
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {saveError && (
            <Alert variant="destructive">
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="scanner-username">Scanner Username</Label>
            <div className="flex gap-2">
              <Input
                id="scanner-username"
                placeholder="Enter username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (usernameError) setUsernameError(null);
                  if (saveError) setSaveError(null);
                }}
                className={usernameError ? 'border-red-500' : ''}
              />
              {username && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(username, 'Username')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            {usernameError && (
              <p className="text-sm text-red-600">{usernameError}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="scanner-password">
              Scanner Password
            </Label>
            <div className="flex gap-2">
              <Input
                id="scanner-password"
                type="text"
                placeholder="Enter scanner password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError(null);
                  if (saveError) setSaveError(null);
                }}
                className={passwordError ? 'border-red-500' : ''}
              />
              <Button
                type="button"
                variant="outline"
                onClick={generatePassword}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {password && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(password, 'Password')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              )}
            </div>
            {passwordError && (
              <p className="text-sm text-red-600">{passwordError}</p>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="scanner-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <Label htmlFor="scanner-enabled">Enable scanner access</Label>
          </div>
          
          <Separator />
          
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={saving || !username}
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsSetupOpen(false)}
                disabled={saving}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
            
            {/* Go to Scanner Page Button */}
            <Button
              variant="secondary"
              onClick={() => {
                window.open('/update-pass', '_blank');
              }}
              className="w-full"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Scanner Page
            </Button>
          </div>
          
          {/* Info section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-800">Scanner Access Information</p>
                <ul className="text-blue-700 mt-1 space-y-1 text-xs">
                  <li>• Workers can use scanner credentials to access /update-pass</li>
                  <li>• Scanner access works for all loyalty programs in this business</li>
                  <li>• Credentials can be shared among multiple workers</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessScannerCredentialsManager;